import { Component, useContext  } from "react";
import { Loader, Button, DoorArrowLeftIcon, Text, Tooltip } from "@fluentui/react-northstar";
import "./InCallSidePanel.css";
import { TeamsFxContext, TeamsFxContextType } from "../Context";
import * as microsoftTeams from "@microsoft/teams-js";
import { executeDoorOpen, verifyDoorOpen } from '../../apis/cyberGateManagementApi/cyberGateApiClient'
import { DoorOpenVerification } from '../../apis/cyberGateManagementApi/types'
import * as misc from '../../utils/misc';

type InCallSidePanelNewProps = {
    teamsFxContext: TeamsFxContextType
}

type InCallSidePanelNewState = {
    isInitializing: boolean,    
    isMeeting: boolean,
    isRetrievingDtmfInfo: boolean,
    isDoorOpenAvailable: boolean,
    hasErrorOccurred: boolean
}

class InCallSidePanelInternal extends Component<InCallSidePanelNewProps, InCallSidePanelNewState> {    
    isPanelShown: boolean = true;

    state: InCallSidePanelNewState = {
        isInitializing: true,
        isMeeting: false,
        isRetrievingDtmfInfo: false,
        isDoorOpenAvailable: false,
        hasErrorOccurred: false
    }
    
    componentDidMount() {
        this.initializeIfNeeded();
    }

    componentDidUpdate() {
        this.initializeIfNeeded();
    }

    componentWillUnmount() {        
        this.isPanelShown = false;
    }

    initializeIfNeeded = () => {
        if (!this.state.isInitializing) {
            return; // Already initialized.
        }
        
        const { inTeams, context } = this.props.teamsFxContext;
        if (inTeams === undefined || context === undefined) {            
            return; // Enclosing application is still initializing.
        }

        this.setState({
            isInitializing : false
        });        

        if (!inTeams) {
            // Nothing has to be done. The enclosing application will show an error message that the app can only be used inside Microsoft Teams.
            return;
        }

        microsoftTeams.app.notifySuccess();

        if (this.isMeeting(context)) {
            this.setState({
                isMeeting: true
            });

            return;
        }

        // The app has been loaded and runs in a side-panel for a 1:1 call.  
        // Now try to retrieve the related active call.
        this.setState({
            isRetrievingDtmfInfo: true
        });

        this.verifyDoorOpen();
    }

    verifyDoorOpen = async () => {
        const { userPrincipalName, chatId } = this.props.teamsFxContext.context;

        try {
            let data: DoorOpenVerification | undefined;
            do {
                data = await verifyDoorOpen(userPrincipalName, chatId);

                if (data.isActiveCallFound) {
                    break;
                }

                // No active call is found. This might happen when the side-panel is opened before the call is established
                // (very likely when calling from Teams to the device). Keep retrying with a 2 second interval until either
                // the call is found or the panel is closed.
                await misc.sleep(2000);

                if (!this.isPanelShown) {
                    return;
                }
            } while (true)

            this.setState(
                {
                    isRetrievingDtmfInfo: false,
                    isDoorOpenAvailable: data.isDtmfConfigured
                }
            );
        }
        catch (error) {
            console.log(`Error occurred while executing verifyDoorOpen: ${error}`);
            
            this.setState(
                {
                    isRetrievingDtmfInfo: false,
                    hasErrorOccurred: true
                }
            );
        }   
    }

    isMeeting = (context: any) => {
        const {chatId} = context

        console.log(`chatId (${typeof chatId}) = ${chatId}`)

        return chatId.includes('meeting');
    }

    openDoor = (context: any) => {
        const {userPrincipalName, chatId} = context
        console.log(`openDoor: userPrincipalName=${userPrincipalName}, chatId=${chatId}`)

        executeDoorOpen(userPrincipalName, chatId)
            .then(data => console.log(data))
            .catch(error => console.log(error))
    }
    
    openDoorButtonTooltip = () => {
        const { isMeeting, hasErrorOccurred, isRetrievingDtmfInfo, isDoorOpenAvailable  } = this.state;

        if (isMeeting) {
            return 'Open-door feature unavailable for meetings' ;           
        }

        if (hasErrorOccurred) {
            return 'An error occurred while checking the configuration.';
        }

        if (isRetrievingDtmfInfo) {
            return 'No CyberGate call found yet...';
        } 
        
        if (isDoorOpenAvailable) {
            return 'Open the door';
        }  

        return 'Please ask your administrator to configure the \'Open-door\xa0code\' for this device.';
    }

    getWrongFrameContextWarningText = (frameContext: any) => {
        switch (frameContext) {
            case microsoftTeams.FrameContexts.content:
                return "This app has no content screen.";
            case microsoftTeams.FrameContexts.settings:
                return "This app has no settings screen.";          
            case microsoftTeams.FrameContexts.meetingStage:
                return "This app has no meeting stage screen.";
            default:
                return `Unknown frame context '${frameContext}'`
        }     
    }
    
    render() {
        const { themeString, context } = this.props.teamsFxContext;
        const { isRetrievingDtmfInfo, isDoorOpenAvailable, isInitializing } = this.state;

        if (isInitializing) {
            return (
                <Loader style={{ margin: 100 }} />
            )
        }

        if (context.frameContext !== microsoftTeams.FrameContexts.sidePanel) {
            let warningText = this.getWrongFrameContextWarningText(context.frameContext);
            
            return (
                <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Text align="center" content={warningText} size="larger" />
                </div>
            )
        }

        return (
            <div className={themeString === "default" ? "" : "dark"}>
                <div className="incallsidepanel page" style={{ display: 'flex', justifyContent: 'center', paddingTop: 70 }}>
                    <Tooltip
                        unstable_pinned={true}
                        unstable_disableTether='all'
                        pointing position='above'
                        align="center"
                        subtle={false}
                        content={this.openDoorButtonTooltip()}
                        trigger={
                            <Button
                                primary
                                iconOnly
                                styles={{ width: 100, height: 100 }}
                                variables={{ backgroundColorIconOnlyHover: themeString === 'contrast' ? '#ffff01' : 'transparent' }}
                                icon={<DoorArrowLeftIcon size="largest" />}
                                disabledFocusable={!isDoorOpenAvailable || isRetrievingDtmfInfo}
                                onClick={() => this.openDoor(context)} />
                        } />
                </div>
            </div>
        )
    };
}

// Inject the required contexts.
export const InCallSidePanel = () => {
    const teamsFxContext = useContext(TeamsFxContext);    

    return (
        <InCallSidePanelInternal teamsFxContext={teamsFxContext}/>
    )
}